/** @format */

import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	Inject,
	input,
	OnDestroy,
	output,
	SimpleChanges
} from '@angular/core';
import { AuthenticationService } from '@shared/authentication/data-access';
import { EventBusService, Events, SnackbarService, WINDOW } from '@shared/utils/services';
import { FavoriteProduct } from '@my-account/data-access/interfaces';
import { MyCustomizations, PersonalizationState } from '@shared/data-access/interfaces';
import { FavoritesIconEnum, FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import {
	IListItemAnalytics,
	IPanelActions,
	IPanelBody,
	ProductAttribute
} from '@shared/favorites/data-access/interfaces';
import { WishListService } from '@shared/favorites/data-access/services';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { ActivatedRoute } from '@angular/router';
import { MyAccountAnalyticsService, Store } from '@shared/data-access/services';

@Component({
	selector: 'shared-favorites-components-auth-icon',
	templateUrl: './favorites-icon.component.html',
	styleUrls: ['./favorites-icon.component.scss'],
	standalone: false
})
export class FavoritesIconComponent implements AfterViewInit, OnDestroy {
	constructor(
		private wishlistService: WishListService,
		private snackbarService: SnackbarService,
		private analyticsService: MyAccountAnalyticsService,
		private eventBusService: EventBusService,
		private store: Store,
		private authenticationService: AuthenticationService,
		private changeDetector: ChangeDetectorRef,
		private activatedRoute: ActivatedRoute,
		@Inject(WINDOW) protected window: Window
	) {}

	ecode = input<string>(null);
	resolvedSku = input<string>(null);
	listProductPersonalization = input<PersonalizationState | Record<string, never>>(null);
	location = input<FavoritesLocationEnum>(FavoritesLocationEnum.FAVORITES);
	bundleProduct = input<any>(null);
	isCustomizable = input<boolean>(false);
	isAuthenticated = input<boolean>(false);
	isSingleSkuProduct = input<boolean>(false);

	/** @description Show/Hide favorites button with text on PDP */
	isButtonWithText = input<boolean>(false);

	customAttributes = input<MyCustomizations>(null);

	listID = input<string>(null);

	product = input<FavoriteProduct>(null);

	listName = input<string>(null);

	listIndex = input<number>(null);

	setAttributeErrors = output<any>();

	openCustomizeTab = output<any>();

	deleteProductEmit = output<any>();

	productIsFavorited = false;
	iconName = 'reaction-love-outlined';
	actionInProgress = false;
	pdpButtonLabel = 'Add product to list';
	pdpButtonText = 'Add to List';

	private ngUnsubscribe: Subject<void> = new Subject();
	private productsInStore: Map<string, string[]> = new Map();

	protected readonly favoritesLocationEnum = FavoritesLocationEnum;

	ngAfterViewInit(): void {
		this.store
			.select('SAVEDPRODUCTS', true)
			.pipe(takeUntil(this.ngUnsubscribe))
			.subscribe({
				next: (productsInStore: any) => {
					if (this.location() === this.favoritesLocationEnum.FAVORITES) {
						// already in favorites. products must be saved already
						this.productIsFavorited = true;
						this.handleProductIsFavoritedChange();
					} else {
						const isSkuAvailable = !!this.resolvedSku() && !this.isSingleSkuProduct();
						this.productsInStore = productsInStore;
						this.checkSkuOrEcodeStatus(isSkuAvailable);
					}

					this.eventBusService.on(Events.AttributeClickEvent, () => {
						// eslint-disable-next-line no-unused-expressions
						this.checkSkuOrEcodeStatus(!!this.resolvedSku());
					});

					this.eventBusService.on(Events.CustomAttributesProcessed, (event) => {
						// fix for a race condition between this component and  custom-default component
						// where the custom attributes may not be processed yet
						this.customAttributes = event.customizedAttributes;
						// eslint-disable-next-line no-unused-expressions
						this.checkSkuOrEcodeStatus();
					});

					// detect changes so that the button UI can update
					this.changeDetector.detectChanges();
				},
				error: (err) => {
					this.analyticsService.generateLogError(
						MyAccountErrorLogKeys.FAVORITES,
						'Failed to get favorites from store',
						err
					);
				}
			});
	}

	ngOnDestroy() {
		this.ngUnsubscribe.next();
		this.ngUnsubscribe.complete();
		this.eventBusService.unsubscribe(Events.AttributeClickEvent);
		this.eventBusService.unsubscribe(Events.CustomAttributesProcessed);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (
			changes.resolvedSku &&
			changes.resolvedSku.currentValue !== changes.resolvedSku.previousValue
		) {
			// eslint-disable-next-line no-unused-expressions
			this.checkSkuOrEcodeStatus(true);
		}
	}

	handleProductIsFavoritedChange() {
		this.pdpButtonText = this.productIsFavorited ? 'Added to List' : 'Add to List';
		this.pdpButtonLabel = this.productIsFavorited
			? 'Delete product from list'
			: 'Add product to list';
		this.setIconName();
	}

	determineFavoritesAction() {
		// If athlete is guest, automatically redirect them to Sign In
		if (!this.isAuthenticated()) {
			this.analyticsService.triggerAnalyticsEvent('SignOut_AddToList');
			// Will handle redirect to sign in page, passing in query parameters if they exist (window.location.search)
			this.authenticationService
				.login({ target: location.pathname + location.search })
				.pipe(take(1))
				.subscribe();
			return;
		}

		if (this.productIsFavorited) {
			this.lockHeartIcon();
			const sku = this.resolvedSku() ?? '';

			// open list panel so athletes can choose which list to remove from
			if (this.location() !== this.favoritesLocationEnum.FAVORITES) {
				this.openListPanel(this.ecode(), sku);
				return;
			}

			// already in favorites so remove from list
			this.deleteFromFavorites();
		} else if (this.location() === this.favoritesLocationEnum.PLP) {
			// if on PLP, open react sidepanel to resolve sku
			this.window.quickview.openProductQuickView(this.ecode(), location.pathname, {
				template: 'favorites',
				callback: () => {
					this.actionInProgress = false;
				}
			});
		} else if (this.location() === this.favoritesLocationEnum.RECOMMENDED) {
			this.window.quickview.openProductQuickView(this.ecode(), 'DataScienceRecs', {
				template: 'favorites',
				additionalData: {
					DSDisplayName: this.activatedRoute.snapshot.queryParams.dsDisplayName,
					DSDiversityLevel: '',
					DSPlacementId: this.activatedRoute.snapshot.queryParams.dsPlacementId,
					DSPosition: this.listIndex,
					DSPriceRerankLevel: this.activatedRoute.snapshot.queryParams.dsPriceRerankLevel,
					DSRecsGCPProject: ''
				},
				callback: () => {
					this.actionInProgress = false;
				}
			});
		} else {
			// Lock heart icon so can't be spammed
			this.lockHeartIcon();

			const productAttributes = this.wishlistService.getProductAttributes(
				this.listProductPersonalization(),
				this.isCustomizable(),
				this.customAttributes()
			);
			const sku = this.wishlistService.determineSkuStamp(
				this.resolvedSku(),
				this.listProductPersonalization(),
				this.customAttributes()
			);
			this.openListPanel(this.ecode(), sku, productAttributes);
		}
	}

	/**
	 * @description Used to check if a sku or ecode is saved in any of the athletes lists.
	 * @param checkForSkuInList boolean to search for sku in saved lists or ecode
	 */
	private checkSkuOrEcodeStatus(checkForSkuInList?: boolean) {
		let skuOrEcode = this.ecode();
		if (checkForSkuInList) {
			skuOrEcode = this.resolvedSku() || '';
		}
		if (this.isCustomizable() && this.customAttributes()?.MyCustomizerMap.designId) {
			// eslint-disable-next-line prefer-template
			skuOrEcode += '-MC-' + this.customAttributes().MyCustomizerMap.designId;
		}

		this.productIsFavorited = !!this.productsInStore.get(skuOrEcode) || false;

		this.handleProductIsFavoritedChange();
	}

	private setIconName() {
		if (this.productIsFavorited) {
			this.iconName = FavoritesIconEnum.FILLED;
		} else {
			this.iconName = FavoritesIconEnum.OUTLINED;
		}
	}

	/** @description Used to set actionInProgress to true and back to false in case callback functions are not triggered */
	private lockHeartIcon() {
		this.actionInProgress = true;
		setTimeout(() => {
			this.actionInProgress = false;
		}, 2000);
	}

	private deleteFromFavorites() {
		let sku = this.resolvedSku() || '';
		if (this.location() === this.favoritesLocationEnum.PDP) {
			if (sku && this.isCustomizable() && this.customAttributes()?.MyCustomizerMap.designId) {
				// eslint-disable-next-line prefer-template
				sku += '-MC-' + this.customAttributes().MyCustomizerMap.designId;
			}
			this.openListPanel(this.ecode(), sku);
			return;
		}

		// Only triggered in MyAccount
		const analyticsObject: IListItemAnalytics = {
			NumberOfLists: 1,
			ProductID: this.ecode(),
			ProductSku: this.wishlistService.removeSkuStamp(sku)
		};

		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		this.wishlistService.deleteProductFromList$([this.product().id]).subscribe(
			() => {
				this.actionInProgress = false;
				// Emit delete event so MyAccount can replace product
				this.deleteProductEmit.emit(this.product().id);
				this.snackbarService.showSnackBar({
					isAuthSnackbar: false,
					showSnackbar: true,
					text: `Removed from ${this.listName()}`,
					link: ''
				});

				this.analyticsService.triggerAnalyticsEvent('RemoveFromList', analyticsObject);
			},
			() => {
				this.actionInProgress = false;
				this.snackbarService.showSnackBar({
					isAuthSnackbar: false,
					showSnackbar: true,
					text: 'Unexpected error. Please try again later.',
					link: this.location() === 'Favorites' ? '' : '/MyAccount/Favorites'
				});
				this.analyticsService.singleErrorToAnalytics('RemoveFromFavorites_Error');
			}
		);
	}

	private openListPanel(ecode: string, sku?: string, productAttributes?: ProductAttribute) {
		const panelBody: IPanelBody = {
			ecode: ecode
		};
		const panelAction = this.productIsFavorited ? 'remove-product' : 'add-product';

		// single sku products are saved at ecode so that it reflects properly on PLPs
		if (sku && !this.isSingleSkuProduct()) {
			panelBody.sku = sku;
		}

		if (productAttributes) {
			panelBody.productAttributes = productAttributes;
		}

		this.analyticsService.triggerAnalyticsEvent('ModalOpen', { ModalName: 'ManageYourSaves' });

		this.window.headerFunctions.openListPanel(
			panelAction,
			panelBody,
			false,
			(panelListActions: IPanelActions) => {
				this.actionInProgress = false;
				const productKey = sku || this.ecode();
				this.wishlistService.manageProductsInStore(
					productKey,
					panelListActions,
					this.productsInStore
				);
			},
			this.location()
		);
	}
}
